import axiosInstance from 'utils/axiosInstance';
import {
  DetailResponseData,
  FormAdmin,
  FormDTO,
  FormList,
  FormResponse,
  FormResponseDetail,
  FormResponseFilter,
  FormResponseListFilter,
  FormResponsesType,
  FormResponseSummary,
  FormsResponse,
  ResponseExport,
  SecondaryFilter,
  SubmitFormDTO,
  TemplateItem,
} from '../types';
import { FormEdit } from '../types/fieldTypes';

export const getForms = async (
  filters: FormList,
): Promise<ApiResponse<FormList>> => {
  const { data } = await axiosInstance.post<ApiResponse<FormList>>(
    'api/Forms/list',
    filters,
  );
  return data;
};

export const editForm = async (
  form: FormData,
): Promise<ApiResponse<FormEdit>> => {
  const { data } = await axiosInstance.post<ApiResponse<FormEdit>>(
    'api/Forms/edit',
    form,
  );
  return data;
};

export const getTemplates = async (): Promise<ApiResponse<TemplateItem[]>> => {
  const { data } = await axiosInstance.get<ApiResponse<TemplateItem[]>>(
    'api/Forms/Templates',
  );
  return data;
};

export const detailFormResponse = async (formInfo: {
  formId: number;
  responseId: number;
  sharedId: number;
}): Promise<ApiResponse<SubmitFormDTO>> => {
  const { data } = await axiosInstance.post<ApiResponse<SubmitFormDTO>>(
    'api/Forms/detailFormResponse',
    formInfo,
  );
  return data;
};

export const getFormById = async (
  formId: number,
): Promise<ApiResponse<FormEdit>> => {
  const { data } = await axiosInstance.get<ApiResponse<FormEdit>>(
    `api/Forms/detailForm/${formId}`,
  );
  return data;
};

export const archiveForm = async (
  formId: number,
): Promise<ApiResponse<null>> => {
  const { data } = await axiosInstance.post<ApiResponse<null>>(
    'api/Forms/Archive/',
    null,
    {
      params: {
        formId,
      },
    },
  );
  return data;
};

export const unArchiveForm = async (
  formId: number,
): Promise<ApiResponse<null>> => {
  const { data } = await axiosInstance.post<ApiResponse<null>>(
    'api/Forms/UnArchive/',
    null,
    {
      params: {
        formId,
      },
    },
  );
  return data;
};

export const unpublishForm = async (
  formId: number,
): Promise<ApiResponse<null>> => {
  const { data } = await axiosInstance.post<ApiResponse<null>>(
    `api/Forms/Unpublished/${formId}`,
  );
  return data;
};

export const getFormResponses = async (
  formId: number,
): Promise<ApiResponse<FormResponseSummary>> => {
  const { data } = await axiosInstance.get<ApiResponse<FormResponseSummary>>(
    `api/Forms/FormResponseInformation/${formId}`,
  );
  return data;
};

export const getFilteredFormResponses = async (
  filter: FormResponseFilter,
): Promise<ApiResponse<FormResponsesType>> => {
  const { data } = await axiosInstance.post<ApiResponse<FormResponsesType>>(
    'api/Forms/Responses',
    filter,
  );
  return data;
};

export const submitFormResponse = async (
  formResponse: FormData,
): Promise<ApiResponse<FormResponse>> => {
  const { data } = await axiosInstance.post<ApiResponse<FormResponse>>(
    'api/Forms/SubmitForm',
    formResponse,
  );
  return data;
};

export const exportFormResponses = async (
  formInfo: ResponseExport,
): Promise<ArrayBuffer> => {
  const { data } = await axiosInstance.post<ArrayBuffer>(
    'api/Forms/ExportResponsesToExcel',
    formInfo,
    {
      responseType: 'arraybuffer',
    },
  );
  return data;
};

export const getFormAdmins = async (formId: number): Promise<FormAdmin> => {
  const { data } = await axiosInstance.get<FormAdmin>(
    `api/Forms/admins/${formId}`,
  );
  return data;
};

export const addFormAdmins = async (info: {
  formId: number;
  adminsId: string[];
}): Promise<ApiResponse<null>> => {
  const { data } = await axiosInstance.post<ApiResponse<null>>(
    'api/Forms/AffectAdmins',
    info,
  );
  return data;
};

export const deleteFormAdmin = async (fromInfo: {
  id: string;
  formId: number;
}): Promise<ApiResponse<number>> => {
  const { data } = await axiosInstance.post<ApiResponse<number>>(
    'api/Forms/UnaffectedAdmin',
    fromInfo,
  );
  return data;
};

export const getResponseDetails = async (
  responseId: number,
): Promise<ApiResponse<FormResponseDetail>> => {
  const { data } = await axiosInstance.get<ApiResponse<FormResponseDetail>>(
    `api/Forms/DetailResponse/${responseId}`,
  );
  return data;
};

export const deleteResponse = async (
  responseId: number,
): Promise<ApiResponse<null>> => {
  const { data } = await axiosInstance.delete<ApiResponse<null>>(
    `api/Forms/DeleteResponse/${responseId}`,
  );
  return data;
};

export const formAffectation = async (info: {
  formId: number;
  affectedType: number;
  affectedValues?: string[];
}): Promise<ApiResponse<null>> => {
  const { data } = await axiosInstance.post<ApiResponse<null>>(
    'api/Forms/Affectation',
    info,
  );
  return data;
};

export const getAffectedItems = async (
  formId: number,
  affectTo: number,
): Promise<ApiResponse<SelectItem[]>> => {
  const { data } = await axiosInstance.post<ApiResponse<SelectItem[]>>(
    'api/Forms/affectedItems',
    {
      formId: formId,
      affectTo: affectTo,
    },
  );
  return data;
};

export const getSharedItems = async (
  formId: number,
  shareWith: number,
): Promise<ApiResponse<SelectItem[]>> => {
  const { data } = await axiosInstance.post<ApiResponse<SelectItem[]>>(
    'api/Forms/sharedItems',
    {
      formId: formId,
      shareWith: shareWith,
    },
  );
  return data;
};

export const formSharing = async (info: {
  formId: number;
  sharedType: number;
  sharedValues?: string[];
}): Promise<ApiResponse<null>> => {
  const { data } = await axiosInstance.post<ApiResponse<null>>(
    'api/Forms/Sharing',
    info,
  );
  return data;
};

export const activateAnonymous = async (
  formId: string,
): Promise<ApiResponse<null>> => {
  const { data } = await axiosInstance.post<ApiResponse<null>>(
    `api/Forms/ActivateAnonymous/${formId}`,
  );
  return data;
};

export const deleteForm = async (
  formId: number,
): Promise<ApiResponse<null>> => {
  const { data } = await axiosInstance.delete<ApiResponse<null>>(
    `api/Forms/Delete/${formId}`,
  );
  return data;
};

export const getResponsesByUser = async (
  formInfo: FormResponseListFilter,
): Promise<ApiResponse<FormResponsesType>> => {
  const { data } = await axiosInstance.post<ApiResponse<FormResponsesType>>(
    'api/Forms/ResponsesByUser',
    formInfo,
  );
  return data;
};

export const getFormStatus = async (): Promise<ApiResponse<SelectItem[]>> => {
  const { data } =
    await axiosInstance.get<ApiResponse<SelectItem[]>>('api/Forms/Statues');
  return data;
};

export const getListPublishedForms = async (): Promise<
  ApiResponse<FormsResponse>
> => {
  const { data } = await axiosInstance.get<ApiResponse<FormsResponse>>(
    'api/Forms/ListPublished',
  );
  return data;
};

export const getListSecondaryForms = async (
  filter: SecondaryFilter,
): Promise<FormDTO[]> => {
  const { data } = await axiosInstance.post<FormDTO[]>(
    'api/Forms/ListPublishedSecondary',
    filter,
  );
  return data;
};

export const duplicateForm = async (formInfo: {
  id: number;
  title: string;
  description: string;
}): Promise<ApiResponse<null>> => {
  const { data } = await axiosInstance.post<ApiResponse<null>>(
    'api/Forms/duplicate',
    formInfo,
  );
  return data;
};

export const downloadFile = async (fileName: string): Promise<ArrayBuffer> => {
  const { data } = await axiosInstance.post<ArrayBuffer>(
    'api/files/DownloadFileByName',
    null,
    {
      params: {
        name: fileName,
      },
      responseType: 'arraybuffer',
    },
  );
  return data;
};

export const detailResponseData = async (responseId: number) => {
  const { data } = await axiosInstance.get<DetailResponseData>(
    `api/Forms/detailresponsedata/${responseId}`,
  );
  return data;
};

export const exportFormResponse = async (
  html: string,
  title: string,
): Promise<ArrayBuffer> => {
  const { data } = await axiosInstance.post<ArrayBuffer>(
    'api/Forms/exportformtopdf/',
    { html, title },
    { responseType: 'arraybuffer' },
  );
  return data;
};
